import { useEffect, useState, Fragment } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Table, Input, Button, Tag, Modal, Segmented, Space, Typography, Flex,
  App as AntApp, Spin, Select, Switch, Alert,
} from 'antd';
import {
  SearchOutlined, DownOutlined, RightOutlined, CloseOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../config';
import { rotuloAreaReceita } from '../utils/areasReceita';
import { hojeIso } from '../utils/dataFmt';
import { usePerms } from '../hooks/usePerms';
import { PRIMEIRO_MES_RECALCULAVEL, rotuloMes } from './administrativo-plano-acao/paginas/dadosSetores';

const { Title, Text } = Typography;

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

const fmt = (v: number) =>
  'R$ ' + Math.abs(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtSigned = (v: number) => (v < 0 ? '−' : '') + fmt(v);

const fmtDate = (s: string | null) => {
  if (!s) return '—';
  const [y, m, d] = s.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

interface CentroItem {
  codigo_canonical: string;
  nome_completo: string;
  tipo: string;
  ativo: boolean;
  data_inicio: string | null;
  data_fim: string | null;
  fechado: boolean;
  receita_bruta: string;
  gasto_direto: string;
  entradas_positivas_em_pagar: string;
  grupo_despesa_horas?: 'DESPESAS_ADMINISTRATIVAS' | 'DESPESAS_COMERCIAIS' | null;
  linha_despesa_horas?: string | null;
  no_anotador?: boolean;
  apontamentos_ano?: number;
}

interface DREProjeto {
  codigo: string;
  nome: string;
  data_inicio: string | null;
  data_fim: string | null;
  fechado: boolean;
  trimestres_janela: string[];
  fator: number;
  receitas: { recBruta: number; comissoes: number; recLiqComercial: number; areas: Record<string, number>; comissoesDetalhe: Record<string, number> };
  custos: { custosOp: number; detalhe: Record<string, number> };
  lucroBruto: number;
  despesas: { despComercial: number; despAdmin: number; outrasDespOp: number; detalhe: { comercial: Record<string, number>; admin: Record<string, number>; outras: Record<string, number> } };
  outrasReceitasOp: { total: number; detalhe: Record<string, number> };
  estorno: { total: number; detalhe: Record<string, number> };
  resultadoOp: number;
  financeiro: { despFin: number; recFin: number; detalhe: { despesas: Record<string, number>; receitas: Record<string, number> } };
  resultadoAntes: number;
  deducoes: { total: number; detalhe: Record<string, number> };
  impostos: { total: number; detalhe: Record<string, number>; modo: string };
  lucroLiq: number;
  investimentos: { total: number; detalhe: Record<string, number> };
  lucroFinal: number;
}

type StatusFiltro = 'abertos' | 'fechados' | 'todos';
type Vista = 'projetos' | 'internos';

const OPCOES_GRUPO = [
  { value: 'DESPESAS_ADMINISTRATIVAS', label: 'Despesas administrativas' },
  { value: 'DESPESAS_COMERCIAIS', label: 'Despesas comerciais' },
  { value: '', label: 'Fica na remuneração (custo operacional)' },
];
// Centro fora do anotador e sem anotação no ano: o destino da hora não se aplica
// (15/10/2026 — "Operações Financeiras" mostrava "fica no custo operacional" e confundia:
// o destino é SÓ da hora anotada; despesa lançada no centro segue a categoria dela).
const semHora = (r: CentroItem) => !r.no_anotador && !(Number(r.apontamentos_ano) || 0);

function lerErro(e: any): string | null {
  const d = e?.response?.data;
  if (!d) return null;
  try {
    const corpo = d.body ? (typeof d.body === 'string' ? JSON.parse(d.body) : d.body) : d;
    return corpo?.erro || null;
  } catch { return d?.erro || null; }
}

function CentrosCustoInner() {
  const { message, modal } = AntApp.useApp();
  const [statusFiltro, setStatusFiltro] = useState<StatusFiltro>('abertos');
  const [centros, setCentros] = useState<CentroItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [busca, setBusca] = useState('');
  const [selecionado, setSelecionado] = useState<string | null>(null);
  const [dreProj, setDreProj] = useState<DREProjeto | null>(null);
  const [loadingDre, setLoadingDre] = useState(false);
  const [expandidos, setExpandidos] = useState<Record<string, boolean>>({});
  const [vista, setVista] = useState<Vista>('projetos');
  const [internos, setInternos] = useState<CentroItem[]>([]);
  const [loadingInt, setLoadingInt] = useState(false);
  const [salvando, setSalvando] = useState<string | null>(null);
  const { pode } = usePerms();
  const podeEditar = pode('financeiro.centros_custo.editar');

  useEffect(() => { if (vista === 'projetos') carregar(); }, [statusFiltro, vista]);
  useEffect(() => { if (vista === 'internos') carregarInternos(); }, [vista]);

  async function carregarInternos() {
    setLoadingInt(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/centros-custo?tipo=estrutural&status=todos`, {
        headers: { Authorization: token },
      });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setInternos(data);
    } catch (e) {
      message.error(lerErro(e) || 'Não consegui carregar os centros internos.');
    } finally { setLoadingInt(false); }
  }

  async function salvarInterno(codigo: string, campos: Record<string, unknown>, avisoDre = false) {
    setSalvando(codigo);
    try {
      const token = await getToken();
      await axios.patch(`${API_URL}/centros-custo/${encodeURIComponent(codigo)}`, campos, {
        headers: { Authorization: token },
      });
      message.success('Salvo.');
      if (avisoDre) {
        message.warning(`Muda a DRE desde ${rotuloMes(PRIMEIRO_MES_RECALCULAVEL)} para esse centro. Recalcule os meses em Financeiro › DRE & Resultado › Margem por setor (botão Recalcular).`, 8);
      }
      await carregarInternos();
    } catch (e) {
      message.error(lerErro(e) || 'Não consegui salvar.');
    } finally { setSalvando(null); }
  }

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/centros-custo?tipo=projeto&status=${statusFiltro}`, {
        headers: { Authorization: token },
      });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setCentros(data);
    } finally { setLoading(false); }
  }

  async function abrirDreProjeto(codigo: string) {
    setSelecionado(codigo);
    setDreProj(null);
    setLoadingDre(true);
    try {
      const token = await getToken();
      const res = await axios.get(
        `${API_URL}/dre/centro-custo/${encodeURIComponent(codigo)}`,
        { headers: { Authorization: token } }
      );
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setDreProj(data);
    } finally { setLoadingDre(false); }
  }

  async function fecharProjeto(codigo: string) {
    const hoje = hojeIso();
    const dataFim = prompt(`Informe a data de término do projeto ${codigo} (formato YYYY-MM-DD):`, hoje);
    if (!dataFim) return;
    try {
      const token = await getToken();
      await axios.patch(`${API_URL}/centros-custo/${encodeURIComponent(codigo)}`, {
        data_fim: dataFim, fechado: true,
      }, { headers: { Authorization: token } });
      message.success('Projeto fechado.');
      await carregar();
    } catch { message.error('Erro ao fechar projeto.'); }
  }

  async function reabrirProjeto(codigo: string) {
    modal.confirm({
      title: `Reabrir o projeto ${codigo}?`,
      okText: 'Reabrir',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.patch(`${API_URL}/centros-custo/${encodeURIComponent(codigo)}`, {
            data_fim: null, fechado: false,
          }, { headers: { Authorization: token } });
          message.success('Projeto reaberto.');
          await carregar();
        } catch { message.error('Erro ao reabrir projeto.'); }
      },
    });
  }

  const filtrados = centros.filter(c =>
    !busca || c.nome_completo.toLowerCase().includes(busca.toLowerCase())
  );

  const colunasInternos: ColumnsType<CentroItem> = [
    {
      title: 'Centro', dataIndex: 'nome_completo', key: 'centro',
      render: (v, r) => (
        <Space size={4}>
          <Text strong>{v}</Text>
          {!r.ativo && <Tag style={{ margin: 0 }}>inativo</Tag>}
        </Space>
      ),
    },
    {
      title: 'Hora anotada aqui vai para', key: 'grupo',
      render: (_, r) => semHora(r) ? <Text type="secondary">não se aplica</Text> : (
        <Select
          showSearch
          optionFilterProp="label"
          style={{ width: 240 }}
          value={r.grupo_despesa_horas || ''}
          options={OPCOES_GRUPO}
          disabled={!podeEditar || salvando === r.codigo_canonical}
          onChange={(v: string) => salvarInterno(r.codigo_canonical, { grupo_despesa_horas: v || null }, true)}
        />
      ),
    },
    {
      title: 'Linha da DRE', key: 'linha',
      render: (_, r) => {
        if (semHora(r)) return <Text type="secondary">não se aplica</Text>;
        const propria = `${r.nome_completo} (horas da equipe)`;
        const atual = r.linha_despesa_horas || '';
        const opcoes = [
          { value: '', label: 'Mesma categoria da remuneração da pessoa' },
          { value: propria, label: `Linha própria: ${propria}` },
        ];
        if (atual && atual !== propria) opcoes.push({ value: atual, label: atual });
        return (
          <Select
            showSearch
            optionFilterProp="label"
            style={{ width: 260 }}
            value={atual}
            options={opcoes}
            disabled={!podeEditar || !r.grupo_despesa_horas || salvando === r.codigo_canonical}
            onChange={(v: string) => salvarInterno(r.codigo_canonical, { linha_despesa_horas: v || null })}
          />
        );
      },
    },
    {
      title: 'Aparece no anotador', key: 'anotador', align: 'center',
      render: (_, r) => (
        <Switch
          checkedChildren="Sim"
          unCheckedChildren="Não"
          checked={!!r.no_anotador}
          disabled={!podeEditar || salvando === r.codigo_canonical}
          onChange={(v) => salvarInterno(r.codigo_canonical, { no_anotador: v })}
        />
      ),
    },
    {
      title: 'Anotações no ano', dataIndex: 'apontamentos_ano', key: 'anotacoes', align: 'right',
      render: (v) => Math.round(Number(v) || 0).toLocaleString('pt-BR'),
    },
  ];

  const columns: ColumnsType<CentroItem> = [
    {
      title: 'Código', dataIndex: 'codigo_canonical', key: 'codigo', width: 160,
      render: (v, r) => (
        <Space size={4}>
          <Text strong style={{ color: '#020D7F' }}>{v}</Text>
          {r.fechado && <Tag style={{ margin: 0 }}>FECHADO</Tag>}
        </Space>
      ),
    },
    {
      title: 'Projeto', dataIndex: 'nome_completo', key: 'nome',
      render: (v) => v.replace(/^P_\d+\s*-\s*/, ''),
    },
    {
      title: 'Início', dataIndex: 'data_inicio', key: 'inicio', width: 100, align: 'center',
      render: (v) => <Text type="secondary" style={{ fontSize: 11 }}>{fmtDate(v)}</Text>,
    },
    {
      title: 'Fim', dataIndex: 'data_fim', key: 'fim', width: 100, align: 'center',
      render: (v) => <Text type="secondary" style={{ fontSize: 11 }}>{fmtDate(v)}</Text>,
    },
    {
      title: 'Receita', dataIndex: 'receita_bruta', key: 'rec', align: 'right',
      render: (v) => <Text style={{ color: '#0a7f4a' }}>{fmt(parseFloat(v))}</Text>,
    },
    {
      title: 'Gasto Direto', dataIndex: 'gasto_direto', key: 'gas', align: 'right',
      render: (v) => {
        const gas = parseFloat(v);
        return <Text style={{ color: gas < 0 ? '#B91626' : '#888' }}>{gas < 0 ? `−${fmt(gas)}` : fmt(gas)}</Text>;
      },
    },
    {
      title: 'Margem', key: 'mar', align: 'right',
      render: (_, r) => {
        const rec = parseFloat(r.receita_bruta);
        const gas = parseFloat(r.gasto_direto);
        const mar = rec + gas;
        return <Text strong>{fmtSigned(mar)}</Text>;
      },
    },
    {
      title: '%', key: 'pct', align: 'right', width: 70,
      render: (_, r) => {
        const rec = parseFloat(r.receita_bruta);
        const gas = parseFloat(r.gasto_direto);
        const mar = rec + gas;
        const pct = rec > 0 ? (mar / rec) * 100 : 0;
        const cor = pct > 30 ? '#0a7f4a' : pct > 0 ? '#B07500' : '#B91626';
        return <Text strong style={{ color: cor }}>{pct.toFixed(1)}%</Text>;
      },
    },
    {
      title: 'Ações', key: 'acoes', align: 'center', width: 180,
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" onClick={() => abrirDreProjeto(r.codigo_canonical)}>DRE</Button>
          {r.fechado ? (
            <Button size="small" onClick={() => reabrirProjeto(r.codigo_canonical)}>Reabrir</Button>
          ) : (
            <Button size="small" onClick={() => fecharProjeto(r.codigo_canonical)}>Fechar</Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px 24px' }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <Title level={4} style={{ margin: 0, color: '#020D7F' }}>
            {vista === 'projetos' ? 'Centros de Custo (Projetos)' : 'Centros de custo internos (horas da equipe)'}
          </Title>
          <Segmented
            value={vista}
            onChange={(v) => setVista(v as Vista)}
            options={[
              { label: 'Projetos', value: 'projetos' },
              { label: 'Centros internos', value: 'internos' },
            ]}
          />
        </Flex>

        {vista === 'internos' ? (
          <>
            <Text type="secondary">
              Vale só para a hora anotada pela equipe num centro interno: aqui você define para onde a parte do salário dessa hora vai na DRE e quais centros o anotador oferece. Despesa lançada no centro segue a categoria dela, como sempre.
            </Text>
            {!podeEditar && (
              <Alert
                type="info"
                showIcon
                message="Somente leitura: quem define o destino dos centros é quem tem a permissão de editar centros de custo."
              />
            )}
            <Table
              rowKey="codigo_canonical"
              columns={colunasInternos}
              dataSource={internos}
              loading={loadingInt}
              size="small"
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
          </>
        ) : (
          <>
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <span />
          <Space wrap>
            <Segmented
              value={statusFiltro}
              onChange={(v) => setStatusFiltro(v as StatusFiltro)}
              options={[
                { label: 'Abertos', value: 'abertos' },
                { label: 'Fechados', value: 'fechados' },
                { label: 'Todos', value: 'todos' },
              ]}
              size="small"
            />
            <Input
              placeholder="Buscar projeto..."
              value={busca}
              onChange={e => setBusca(e.target.value)}
              prefix={<SearchOutlined style={{ color: '#aaa' }} />}
              style={{ width: 260 }}
              allowClear
              size="small"
            />
            <Text type="secondary" style={{ fontSize: 12 }}>{filtrados.length} projetos</Text>
          </Space>
        </Flex>

        <Table
          rowKey="codigo_canonical"
          columns={columns}
          dataSource={filtrados}
          loading={loading}
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: [25, 50, 100, 200] }}
          rowClassName={(r) => r.fechado ? 'row-fechado' : ''}
          scroll={{ x: 'max-content' }}
        />
          </>
        )}
      </Space>

      {/* Modal DRE do Projeto */}
      <Modal
        open={!!selecionado}
        onCancel={() => { setSelecionado(null); setDreProj(null); }}
        footer={null}
        width={800}
        title={
          dreProj ? (
            <div>
              <Text strong style={{ color: '#020D7F' }}>{dreProj.nome}</Text>
              <div style={{ fontSize: 11, color: '#888', marginTop: 4, fontWeight: 400 }}>
                {fmtDate(dreProj.data_inicio)} → {fmtDate(dreProj.data_fim) !== '—' ? fmtDate(dreProj.data_fim) : 'hoje (aberto)'} ·
                Trimestres: {dreProj.trimestres_janela.join(', ') || 'nenhum'} ·
                Fator: {(dreProj.fator * 100).toFixed(2)}%
              </div>
            </div>
          ) : 'DRE do projeto'
        }
        destroyOnClose
      >
        {loadingDre || !dreProj ? (
          <Flex justify="center" align="center" style={{ minHeight: 200 }}>
            <Spin />
          </Flex>
        ) : (
          <DRETable dre={dreProj} expandidos={expandidos}
            toggle={(id: string) => setExpandidos(e => ({ ...e, [id]: !e[id] }))} />
        )}
      </Modal>
    </div>
  );
}

function DRETable({ dre, expandidos, toggle }: { dre: DREProjeto; expandidos: Record<string, boolean>; toggle: (id: string) => void }) {
  const grupos = [
    { id: 'rec', label: 'RECEITA BRUTA TOTAL', total: dre.receitas.recBruta, tipo: 'receita', detalhe: dre.receitas.areas, isArea: true },
    { id: 'com', label: 'COMISSÕES DE VENDAS', total: dre.receitas.comissoes, tipo: 'despesa', detalhe: dre.receitas.comissoesDetalhe },
    { id: 'recLiq', label: 'RECEITA LÍQUIDA COMERCIAL', total: dre.receitas.recLiqComercial, tipo: 'resultado' },
    { id: 'custos', label: 'CUSTOS OPERACIONAIS', total: dre.custos.custosOp, tipo: 'despesa', detalhe: dre.custos.detalhe, isDireto: true },
    { id: 'lucroBruto', label: 'LUCRO BRUTO', total: dre.lucroBruto, tipo: 'resultado' },
    { id: 'dc', label: 'DESPESAS COMERCIAIS', total: dre.despesas.despComercial, tipo: 'despesa', detalhe: dre.despesas.detalhe.comercial },
    { id: 'da', label: 'DESPESAS ADMINISTRATIVAS', total: dre.despesas.despAdmin, tipo: 'despesa', detalhe: dre.despesas.detalhe.admin },
    { id: 'od', label: 'OUTRAS DESPESAS OPERACIONAIS', total: dre.despesas.outrasDespOp, tipo: 'despesa', detalhe: dre.despesas.detalhe.outras },
    { id: 'oro', label: 'OUTRAS RECEITAS OPERACIONAIS', total: dre.outrasReceitasOp.total, tipo: 'receita', detalhe: dre.outrasReceitasOp.detalhe },
    { id: 'est', label: 'ESTORNO', total: dre.estorno.total, tipo: 'receita', detalhe: dre.estorno.detalhe },
    { id: 'resOp', label: 'RESULTADO OPERACIONAL', total: dre.resultadoOp, tipo: 'resultado' },
    { id: 'df', label: 'DESPESAS FINANCEIRAS', total: dre.financeiro.despFin, tipo: 'despesa', detalhe: dre.financeiro.detalhe.despesas },
    { id: 'rf', label: 'RECEITAS FINANCEIRAS', total: dre.financeiro.recFin, tipo: 'receita', detalhe: dre.financeiro.detalhe.receitas },
    { id: 'resAntes', label: 'RESULTADO ANTES DOS TRIBUTOS', total: dre.resultadoAntes, tipo: 'resultado' },
    { id: 'ded', label: 'DEDUÇÕES', total: dre.deducoes.total, tipo: 'despesa', detalhe: dre.deducoes.detalhe },
    { id: 'imp', label: 'IMPOSTOS', total: dre.impostos.total, tipo: 'despesa', detalhe: dre.impostos.detalhe },
    { id: 'lucroLiq', label: 'LUCRO LÍQUIDO', total: dre.lucroLiq, tipo: 'resultado' },
    { id: 'inv', label: 'INVESTIMENTOS', total: dre.investimentos.total, tipo: 'despesa', detalhe: dre.investimentos.detalhe },
    { id: 'lucroFinal', label: 'LUCRO FINAL', total: dre.lucroFinal, tipo: 'resultado' },
  ];

  const cor = (tipo: string, val: number) => {
    if (tipo === 'resultado') return val >= 0 ? '#0a7f4a' : '#B91626';
    if (tipo === 'receita') return '#0a7f4a';
    return val < 0 ? '#B91626' : '#333';
  };

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
      <tbody>
        {grupos.map(g => {
          const exp = expandidos[g.id];
          const hasDetail = g.detalhe && Object.keys(g.detalhe).length > 0;
          const isResult = g.tipo === 'resultado';
          return (
            <Fragment key={g.id}>
              <tr style={{
                borderBottom: '1px solid #F0F1F5',
                background: isResult ? '#FAFBFC' : '#fff',
                fontWeight: isResult ? 600 : 500,
              }}>
                <td style={{ padding: '7px 8px', cursor: hasDetail ? 'pointer' : 'default' }}
                  onClick={() => hasDetail && toggle(g.id)}>
                  <Space size={4}>
                    {hasDetail && (exp ? <DownOutlined style={{ fontSize: 10 }} /> : <RightOutlined style={{ fontSize: 10 }} />)}
                    <span>{g.label}</span>
                    {(g as any).isDireto && <Text type="success" style={{ fontSize: 10 }}>(direto)</Text>}
                  </Space>
                </td>
                <td style={{ padding: '7px 8px', textAlign: 'right', color: cor(g.tipo, g.total), minWidth: 120 }}>
                  {fmtSigned(g.total)}
                </td>
              </tr>
              {exp && hasDetail && Object.entries(g.detalhe || {}).sort((a, b) => (a[1] as number) - (b[1] as number)).map(([k, v]) => (
                <tr key={`${g.id}-${k}`} style={{ background: '#FAFBFC' }}>
                  <td style={{ padding: '4px 8px 4px 28px', color: '#555', fontWeight: 400, fontSize: 11 }}>
                    {(g as any).isArea ? rotuloAreaReceita(k) : k}
                  </td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', color: cor(g.tipo, v as number), fontWeight: 400, fontSize: 11 }}>
                    {fmtSigned(v as number)}
                  </td>
                </tr>
              ))}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

export default function CentrosCusto() {
  return (
    <AntApp>
      <CentrosCustoInner />
    </AntApp>
  );
}
